import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ConfigService } from "./config.service";
import { Indicator } from "../interfaces/indicator";
import { Stakeholder } from "../interfaces/stakeholder";
import { Map } from "../interfaces/map";
import { Submission } from "../interfaces/submission";
import { IndicatorValues} from "../interfaces/indicator-values";

@Injectable({
    providedIn: 'root'
})
export class IndicatorsService {
    private indicators: Indicator[];
    private GROUP_SIZE: number = 5;

    constructor(private http: HttpClient, private config: ConfigService) {
        this.GROUP_SIZE = this.config.get("stakeholderGroupSize");
    }

    public load(): Promise<Indicator[]> {
        return new Promise((resolve, reject) => {
            this.http.get(this.config.get("api.url") + "/indicators", {
                headers: (new HttpHeaders()).append("X-No-Auth", "True")
            }).subscribe(
                (data: Indicator[]) => {
                    console.info("indicators", data);
                    this.indicators = data;
                    resolve(data);
                },
                err => {
                    console.error("Error fetching indicators", err);
                    reject(err);
                }
            );
        });
    }

    public getIndicators(): Indicator[] {
        return this.indicators;
    }

    public getIndicatorAcceptable(map: Map, stakeholder: Stakeholder, submission: Submission): boolean {
        return this.stakeholderIndicator(stakeholder, submission.indicatorValues);
    }

    public stakeholderIndicator(stakeholder: Stakeholder, currentValues: IndicatorValues): boolean {
        const newVal = currentValues[stakeholder.indKey];
        if (stakeholder.direction === "lower") {
            return newVal < stakeholder.threshold;
        }
        return newVal > stakeholder.threshold;
    }

    public toSignificantDigits(indicator: Indicator, value: number): number {
        if (indicator && indicator.significantDigits) {
            return Number.parseFloat(value.toFixed(indicator.significantDigits));
        }
        return Math.round(value);
    }

    public prettify(indicator: Indicator, value: number): string {
        return value && indicator ? value.toLocaleString(undefined, {
            minimumFractionDigits: indicator.significantDigits,
            maximumFractionDigits: indicator.significantDigits,
        }) : "0";
    }

    public customizeIndicators(indicators: Indicator[]): any[] {
        const out: any[] = [];
        const stakeholders: any = {};

        indicators.forEach(ind => {
            const o: any = Object.assign({}, ind, {
                target: [],
                stakeholders: [],
            });

            ind.stakeholders.forEach(stk => {
                const s: any = Object.assign({}, stk, { indKey: ind.key });
                stakeholders[s.stakeholderGroup] = stakeholders[s.stakeholderGroup] || [];
                stakeholders[s.stakeholderGroup].push(s);
            });
            out.push(o);
        });

        Object.keys(stakeholders).forEach(key => {
            const arr = stakeholders[key];
            arr.sort(this.sortStakeholders);
        });

        this.reduceIndicatorStakeholderCounts(stakeholders);
        Object.keys(stakeholders).forEach(k => {
            stakeholders[k].slice(0, this.GROUP_SIZE).forEach(stk => {
                const ind = out.find(ind => ind.key === stk.indKey);
                ind.stakeholders.push(stk);
            });
        });

        return out;
    }

    private reduceIndicatorStakeholderCounts(stakeholders: any): void {
        const MAX_LOOP = 5;
        for (
            let loopCount = 0, counts = this.getStkIndOverCounts(stakeholders);
            loopCount < MAX_LOOP && counts;
            (counts = this.getStkIndOverCounts(stakeholders), loopCount++)
        ) {
            Object.keys(counts).forEach(k => {
                let toRemove = this.GROUP_SIZE;
                const sArrs = (Object as any).values(stakeholders);
                while (toRemove > 0) {
                    let found;
                    for (const arr of sArrs) {
                        const rm = arr.findIndex(o => o.priority === toRemove && o.indKey === k);
                        if (rm > -1) {
                            if (arr.length > this.GROUP_SIZE) {
                                found = arr.splice(rm, 1);
                            } else {
                                toRemove--;
                            }
                            break;
                        }
                    }

                    if (found) {
                        break;
                    }
                }
            });
        }
    }

    private getStkIndOverCounts(stakeholders: any[]): any {
        const counts: any = (Object as any).values(stakeholders).reduce(
            (m, arr) => (arr.slice(0, this.GROUP_SIZE).forEach(stk => m[stk.indKey] = (m[stk.indKey] || 0) + 1), m),
            {}
        );

        return Object.keys(counts).reduce((m: any, k: string) => {
            if (counts[k] >= this.GROUP_SIZE) {
                m = m || {};
                m[k] = counts[k];
            }
            return m;
        }, null);
    }

    private sortStakeholders(a: any, b: any): number {
        return (a.priority < b.priority ? -1 : (a.priority === b.priority ? (a.name < b.name ? -1 : 1) : 1));
    }
}
